import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useFamilyStore } from "../stores/familyStore";
import { clearPreviewMode } from "../components/PreviewBanner";
import type { Kid } from "@starquest/core";

type Step = "code" | "pick" | "pin" | "nopin";

type KidSummary = { kidId: string; name: string; avatar: string; nickname?: string; hasPinSet?: boolean };

// Stored family data shape
type StoredFamily = { code: string; familyId: string; familyName: string; kids: KidSummary[]; lastKidId?: string };

function loadStoredFamily(): StoredFamily | null {
  try {
    const raw = localStorage.getItem("sq_last_family");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function KidLogin() {
  const navigate = useNavigate();
  const { selectKid } = useFamilyStore();

  const storedFamily = loadStoredFamily();
  const storedLastKid = storedFamily?.lastKidId
    ? storedFamily.kids.find((k) => k.kidId === storedFamily.lastKidId) ?? null
    : null;

  const [step,       setStep]      = useState<Step>(
    storedFamily && storedLastKid
      ? (storedLastKid as any).hasPinSet === false ? "nopin" : "pin"
      : storedFamily ? "pick" : "code"
  );
  const [code,       setCode]      = useState(storedFamily?.code ?? "");
  const [familyId,   setFamilyId]  = useState(storedFamily?.familyId ?? "");
  const [familyName, setFamilyName]= useState(storedFamily?.familyName ?? "");
  const [kids,       setKids]      = useState<KidSummary[]>(storedFamily?.kids ?? []);
  const [selectedKid, setPick]     = useState<KidSummary | null>(storedLastKid);
  const [pin,        setPin]       = useState("");
  const [error,      setError]     = useState("");
  const [loading,    setLoading]   = useState(false);

  async function lookupCode() {
    if (!code.trim()) return;
    setError(""); setLoading(true);
    try {
      const res = await api.familyLookup(code.trim().toUpperCase());
      setFamilyId(res.familyId);
      setFamilyName(res.familyName);
      setKids(res.kids);
      // Remember this family for next time
      const toStore: StoredFamily = { code: code.trim().toUpperCase(), familyId: res.familyId, familyName: res.familyName, kids: res.kids };
      localStorage.setItem("sq_last_family", JSON.stringify(toStore));
      setStep("pick");
    } catch { setError("Family code not found — check with your parent"); }
    finally { setLoading(false); }
  }

  // On mount: silently refresh family data so hasPinSet is always current.
  // This fixes the case where a parent set/reset a PIN after the kid's last visit.
  useEffect(() => {
    if (!code) return;
    api.familyLookup(code).then((res: { familyId: string; familyName: string; kids: KidSummary[] }) => {
      setKids(res.kids);
      // Update the cache with fresh data (keep lastKidId)
      const existing = storedFamily;
      localStorage.setItem("sq_last_family", JSON.stringify({
        code,
        familyId: res.familyId,
        familyName: res.familyName,
        kids: res.kids,
        lastKidId: existing?.lastKidId,
      }));
      // If we're on the "nopin" screen but the kid now has a PIN → advance to PIN entry
      if (step === "nopin" && selectedKid) {
        const fresh = res.kids.find((k) => k.kidId === selectedKid.kidId);
        if (fresh?.hasPinSet) {
          setPick(fresh);
          setStep("pin");
        }
      }
      // If we're on the picker, refresh each kid's hasPinSet so badges are accurate
    }).catch(() => {}); // silent — stale cache is an acceptable fallback
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function changeFamily() {
    localStorage.removeItem("sq_last_family");
    setStep("code");
    setCode("");
    setFamilyId("");
    setFamilyName("");
    setKids([]);
    setError("");
  }

  async function loginWithPin() {
    if (!selectedKid || pin.length < 5) return;
    setError(""); setLoading(true);
    try {
      const res = await api.kidLogin(familyId, selectedKid.kidId, pin);
      localStorage.setItem("sq_token",  res.token);
      localStorage.setItem("sq_kid_id", selectedKid.kidId);
      clearPreviewMode(); // remove any lingering parent preview flag
      // Remember this family + this kid for next login
      const refreshed: StoredFamily = { code, familyId, familyName, kids, lastKidId: selectedKid.kidId };
      localStorage.setItem("sq_last_family", JSON.stringify(refreshed));
      selectKid(res.kid as Kid);
      navigate("/kid");
    } catch (e: any) {
      if (e.code === "BLOCKED") {
        setError("Your account is blocked — ask a parent");
      } else if (e.code === "NO_PIN") {
        setError("No PIN set yet — ask a parent to set one up for you in the Family Hub");
      } else {
        setError("Wrong PIN — try again");
      }
      setPin("");
    }
    finally { setLoading(false); }
  }

  const page: React.CSSProperties = {
    minHeight: "100vh", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    background: "radial-gradient(ellipse at 50% 0%, #2a1f5c, #05050f)",
    padding: 24, gap: 24,
  };
  const card: React.CSSProperties = {
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 20, padding: 32, width: "100%", maxWidth: 380,
    display: "flex", flexDirection: "column", gap: 16,
  };
  const input: React.CSSProperties = {
    padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(0,0,0,0.3)", color: "#fff", fontFamily: "var(--font-display)",
    fontWeight: 700, fontSize: 20, textAlign: "center", letterSpacing: "0.15em", width: "100%",
  };
  const btn: React.CSSProperties = {
    padding: "14px 0", borderRadius: 12, background: "linear-gradient(180deg,#ffe257,#ffd700)",
    color: "#1a1a2e", fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 15,
    cursor: "pointer", border: "none", width: "100%",
  };
  const ghost: React.CSSProperties = {
    ...btn, background: "rgba(255,255,255,0.06)", color: "rgba(240,240,255,0.7)",
    border: "1px solid rgba(255,255,255,0.1)",
  };

  return (
    <div style={page}>
      {/* Always-visible back to landing button */}
      <button
        onClick={() => navigate("/")}
        style={{
          position: "absolute", top: 20, left: 20,
          background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 12, padding: "8px 14px",
          color: "rgba(240,240,255,0.7)", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13,
          cursor: "pointer",
        }}
      >
        ← Back
      </button>
      <div style={{ fontSize: 48 }}>⭐</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 28, color: "#fff" }}>
        StarQuest
      </div>

      {step === "code" && (
        <div style={card}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, textAlign: "center" }}>
            Enter your family code
          </div>
          <div style={{ fontSize: 13, color: "rgba(240,240,255,0.5)", textAlign: "center" }}>
            Ask a parent for your family code
          </div>
          <input
            style={input} placeholder="e.g. STARWARS"
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && lookupCode()}
            autoFocus
          />
          {error && <div style={{ color: "#ff6b6b", fontSize: 13, textAlign: "center" }}>{error}</div>}
          <button style={btn} onClick={lookupCode} disabled={loading || !code.trim()}>
            {loading ? "Looking up…" : "Continue →"}
          </button>
        </div>
      )}

      {step === "pick" && (
        <div style={card}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, textAlign: "center" }}>
            {familyName}
          </div>
          <div style={{ fontSize: 13, color: "rgba(240,240,255,0.5)", textAlign: "center" }}>
            Who's playing?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(kids.length, 3)}, 1fr)`, gap: 10 }}>
            {kids.map((k) => (
              <button key={k.kidId}
                onClick={() => { setPick(k); setPin(""); setStep(k.hasPinSet === false ? "nopin" : "pin"); }}
                style={{ padding: "16px 8px", borderRadius: 14, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ fontSize: 36 }}>{k.avatar}</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, color: "#fff" }}>
                  {k.nickname || k.name}
                </div>
                {k.hasPinSet === false && (
                  <div style={{ fontSize: 9, color: "rgba(251,191,36,0.7)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}>NO PIN SET</div>
                )}
              </button>
            ))}
          </div>
          <button style={ghost} onClick={changeFamily}>🔄 Different family?</button>
        </div>
      )}

      {step === "pin" && selectedKid && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48 }}>{selectedKid.avatar}</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, marginTop: 8 }}>
              Hi {selectedKid.nickname || selectedKid.name}!
            </div>
          </div>
          <div style={{ fontSize: 13, color: "rgba(240,240,255,0.5)", textAlign: "center" }}>
            Enter your PIN
          </div>
          <input
            style={{ ...input, letterSpacing: "0.3em" }}
            type="password" inputMode="numeric" maxLength={10}
            placeholder="•••••"
            value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && loginWithPin()}
            autoFocus
          />
          {error && <div style={{ color: "#ff6b6b", fontSize: 13, textAlign: "center" }}>{error}</div>}
          <button style={btn} onClick={loginWithPin} disabled={loading || pin.length < 5}>
            {loading ? "Logging in…" : "Let's go! →"}
          </button>
          <button style={ghost} onClick={() => { setPick(null); setPin(""); setStep("pick"); setError(""); }}>
            👤 Not me? Choose a different explorer
          </button>
        </div>
      )}
      {step === "nopin" && selectedKid && (
        <div style={card}>
          <div style={{ fontSize: 52, textAlign: "center" }}>{selectedKid.avatar}</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, textAlign: "center" }}>
            Hi {selectedKid.nickname || selectedKid.name}!
          </div>
          <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 14, padding: "16px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, color: "#fbbf24", marginBottom: 6 }}>
              No PIN set up yet
            </div>
            <div style={{ fontSize: 13, color: "rgba(240,240,255,0.6)", lineHeight: 1.5 }}>
              Ask a parent or guardian to set your PIN in the <strong style={{ color: "#fbbf24" }}>Family Hub</strong> so you can log in.
            </div>
          </div>
          <button style={ghost} onClick={() => { setPick(null); setStep("pick"); }}>
            ← Choose a different explorer
          </button>
        </div>
      )}
    </div>
  );
}
